import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import MultiPhotoCapture from "@/components/MultiPhotoCapture";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Sparkles, Trash2, Camera, Video } from "lucide-react";

const EMPTY = { title: "", subject_id: "", kelas: "1", type: "online", instructions: "", questions: [], answer_key: "", rubric: "", weight: 1, start_date: "", due_date: "", ai_enabled: false, photo_paths: [], video_url: "" };

export default function AssignmentDialog({ open, onOpenChange, subjects, lockedSubjectId = null, defaultKelas = null, onCreated }) {
  const { user } = useAuth();
  const waliKelas = user?.teacher_type === "wali" && user?.wali_kelas ? user.wali_kelas : null;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      const k = waliKelas || defaultKelas;
      setForm({ ...EMPTY, subject_id: lockedSubjectId || subjects[0]?.id || "", kelas: k ? String(k) : "1" });
    }
  }, [open, lockedSubjectId, defaultKelas, subjects, waliKelas]);

  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, { no: f.questions.length + 1, text: "", type: "isian", answer: "" }] }));
  const updQ = (i, k, v) => setForm((f) => { const q = [...f.questions]; q[i] = { ...q[i], [k]: v }; return { ...f, questions: q }; });
  const delQ = (i) => setForm((f) => ({ ...f, questions: f.questions.filter((_, x) => x !== i).map((q, n) => ({ ...q, no: n + 1 })) }));

  const save = async () => {
    if (!form.title || !form.subject_id) { toast.error("Judul & mapel wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { ...form, kelas: parseInt(form.kelas), weight: parseInt(form.weight) || 1,
        start_date: form.start_date || null, due_date: form.due_date || null };
      await api.post("/assignments", payload);
      toast.success("Tugas dibuat & notifikasi dikirim ke murid");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error("Gagal membuat tugas");
    } finally { setSaving(false); }
  };

  const lockedSubject = subjects.find((s) => s.id === lockedSubjectId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Buat Tugas Baru{lockedSubject ? ` · ${lockedSubject.name}` : ""}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Judul Tugas</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-xl" data-testid="assignment-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            {!lockedSubjectId && (
              <div>
                <Label>Mata Pelajaran</Label>
                <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="assignment-subject-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className={lockedSubjectId ? "col-span-2" : ""}>
              <Label>Kelas</Label>
              <Select value={form.kelas} onValueChange={(v) => setForm({ ...form, kelas: v })} disabled={!!waliKelas}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="assignment-kelas-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Jenis Tugas</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="assignment-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Tugas Online (kuis)</SelectItem>
                <SelectItem value="pr">PR / Tugas Rumah (unggah foto)</SelectItem>
                <SelectItem value="latihan">Tugas Latihan (foto hasil)</SelectItem>
                <SelectItem value="catatan">Tugas Catatan (foto catatan)</SelectItem>
                <SelectItem value="tatap_muka">Tugas Tatap Muka (nilai manual guru)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Instruksi / Soal</Label><Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1 rounded-xl" data-testid="assignment-instructions-input" /></div>

          <div>
            <Label className="flex items-center gap-1.5"><Video className="w-4 h-4 text-rose-600" /> Link Video / Materi (opsional)</Label>
            <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="mt-1 rounded-xl" placeholder="Tempel link YouTube atau link apa pun" data-testid="assignment-video-input" />
            <p className="text-xs text-slate-500 mt-1">Contoh video pembelajaran. Link YouTube otomatis bisa ditonton langsung oleh murid.</p>
          </div>

          {form.type !== "tatap_muka" && (
            <div>
              <Label className="flex items-center gap-1.5"><Camera className="w-4 h-4 text-teal-600" /> Foto Soal dari Buku (opsional, tanpa batas)</Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">Foto beberapa halaman buku langsung dari kamera belakang atau ambil dari galeri. Semua foto akan dilihat murid.</p>
              <MultiPhotoCapture category="assignment" testIdPrefix="assignment-photo" label="Foto Soal" facingMode="environment" paths={form.photo_paths} onChange={(paths) => setForm((f) => ({ ...f, photo_paths: paths }))} />
            </div>
          )}

          {form.type === "online" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Soal & Kunci Jawaban</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={addQ} data-testid="add-question-btn"><Plus className="w-4 h-4 mr-1" /> Soal</Button>
              </div>
              {form.questions.map((q, i) => (
                <div key={i} className="flex gap-2 items-start bg-slate-50 rounded-xl p-2">
                  <span className="text-sm text-slate-400 mt-2 w-5">{q.no}.</span>
                  <div className="flex-1 space-y-2">
                    <Input value={q.text} onChange={(e) => updQ(i, "text", e.target.value)} placeholder="Pertanyaan" className="rounded-lg h-9" />
                    <Input value={q.answer} onChange={(e) => updQ(i, "answer", e.target.value)} placeholder="Jawaban benar" className="rounded-lg h-9" />
                  </div>
                  <button type="button" onClick={() => delQ(i)} className="text-rose-500 mt-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}

          {form.type !== "tatap_muka" && (<>
            <div><Label>Kunci Jawaban (ringkas)</Label><Textarea value={form.answer_key} onChange={(e) => setForm({ ...form, answer_key: e.target.value })} className="mt-1 rounded-xl" placeholder="Digunakan AI untuk mencocokkan jawaban" /></div>
            <div><Label>Rubrik Penilaian</Label><Textarea value={form.rubric} onChange={(e) => setForm({ ...form, rubric: e.target.value })} className="mt-1 rounded-xl" /></div>
          </>)}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal Mulai</Label><Input type="date" value={form.start_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>Tenggat</Label><Input type="date" value={form.due_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 rounded-xl" data-testid="assignment-due-input" /></div>
          </div>
          {["catatan", "latihan", "pr"].includes(form.type) && (
            <div className="flex items-center justify-between bg-indigo-50 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm font-medium text-indigo-800">Aktifkan Analisis AI</p>
                  <p className="text-xs text-indigo-600">AI membaca tulisan tangan & memberi saran nilai</p>
                </div>
              </div>
              <Switch checked={form.ai_enabled} onCheckedChange={(v) => setForm({ ...form, ai_enabled: v })} data-testid="assignment-ai-toggle" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="save-assignment-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Buat Tugas</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
